import { Controller } from "@hotwired/stimulus"
import QRCode from "qrcode"

export default class extends Controller {
  static targets = [
    "form", "customField", "customAmount", "selectedAmount", "submit", "error",
    "paymentPanel", "qrCode", "qrAmount", "statusText"
  ]
  static values = { createUrl: String }

  connect() {
    this.amount = "10"
    this.custom = false
    this.pollTimer = null
  }

  disconnect() {
    this.stopPolling()
  }

  selectFixed(event) {
    this.custom = false
    this.amount = event.target.value
    this.customFieldTarget.hidden = true
    this.renderAmount()
  }

  selectCustom() {
    this.custom = true
    this.customFieldTarget.hidden = false
    this.customAmountTarget.focus()
    this.chooseCustom()
  }

  chooseCustom() {
    this.custom = true
    this.amount = this.customAmountTarget.value
    const customRadio = this.formTarget.querySelector('input[value="custom"]')
    customRadio.checked = true
    this.renderAmount()
  }

  async create(event) {
    event.preventDefault()
    this.hideError()
    this.submitTarget.disabled = true

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ amount: this.amount, custom: this.custom })
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Unable to create payment")

      await this.showPayment(data)
      this.startPolling(data.id)
    } catch (error) {
      this.showError(error.message)
      this.submitTarget.disabled = false
    }
  }

  async showPayment(order) {
    this.paymentPanelTarget.hidden = false
    this.qrAmountTarget.textContent = `¥${Number(order.amount).toFixed(2)}`
    this.statusTextTarget.textContent = "Waiting for payment"
    this.qrCodeTarget.replaceChildren()

    const canvas = document.createElement("canvas")
    await QRCode.toCanvas(canvas, order.code_url, {
      width: 224,
      margin: 1,
      color: { dark: "#111827", light: "#ffffff" },
      errorCorrectionLevel: "M"
    })
    this.qrCodeTarget.appendChild(canvas)
    this.paymentPanelTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  startPolling(orderId) {
    this.stopPolling()
    this.pollTimer = window.setInterval(() => this.poll(orderId), 3000)
  }

  stopPolling() {
    if (this.pollTimer) window.clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  async poll(orderId) {
    try {
      const response = await fetch(`/payment_orders/${orderId}`, { headers: { "Accept": "application/json" } })
      if (!response.ok) return
      const order = await response.json()

      if (order.status === "credited") {
        this.statusTextTarget.textContent = "Payment received and balance credited"
        this.paymentPanelTarget.dataset.status = "credited"
        this.stopPolling()
        this.submitTarget.disabled = false
      } else if (["failed", "expired"].includes(order.status)) {
        this.statusTextTarget.textContent = order.failure_message || `Payment ${order.status}`
        this.paymentPanelTarget.dataset.status = order.status
        this.stopPolling()
        this.submitTarget.disabled = false
      } else if (order.status === "paid") {
        this.statusTextTarget.textContent = "Payment received, crediting balance"
      }
    } catch (_) {
      // A later poll can recover from a transient network failure.
    }
  }

  renderAmount() {
    const numeric = Number(this.amount)
    this.selectedAmountTarget.textContent = Number.isFinite(numeric) && numeric > 0 ? `¥${numeric.toFixed(2)}` : "¥0.00"
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  hideError() {
    this.errorTarget.hidden = true
  }
}
